import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Typography
} from "@material-ui/core";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import styled from "styled-components";
import { useAlert } from "react-alert";
import { useTranslation } from "react-i18next";
import Group from "../../../waybee-ui/Group";
import Heading from "../../../waybee-ui/Heading";
import Button from "../../../waybee-ui/Button";
import ConfirmAlert from "../../../waybee-ui/ConfirmAlert";
import GeneralBoardService from "../../../services/GeneralBoardService";
import {
  ABSENCE_BOARD_SETTINGS_KEYS,
  DEFAULT_GENERAL_BOARD_CONFIG,
  GRADES_BOARD_COLUMN_KEY,
  getBoardHighlights,
  isBoardColumnVisible,
  normalizeGeneralBoardConfig
} from "../../../utils/generalBoardConfig";
import {
  isDesistenteKind,
  normalizeHighlights
} from "../../../utils/generalBoardHighlights";
import HighlightRuleDialog from "./HighlightRuleDialog";

const PageHeader = styled.div`
  margin-bottom: 8px;
`;

const PageSubtitle = styled(Typography)`
  && {
    color: #6b6b6b;
    margin-top: 4px;
    margin-bottom: 16px;
  }
`;

const Section = styled.div`
  margin-bottom: 8px;
  border: 1px solid #e4e4e4;
  border-radius: 10px;
  background: #fff;
  overflow: hidden;
`;

const SectionHeader = styled.div`
  padding: 16px 18px 14px;
  border-bottom: 1px solid #f0f0f0;
  background: #fafafa;
`;

const SectionTitle = styled(Typography)`
  && {
    font-weight: 600;
    font-size: 18px;
    color: #333;
  }
`;

const SectionHint = styled(Typography)`
  && {
    color: #777;
    margin-top: 4px;
    font-size: 14px;
  }
`;

const SectionBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 18px 14px;
`;

const FooterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 0 18px 16px;
`;

const LoadContainer = styled.div`
  text-align: center;
  margin: 24px 8px;
`;

const HighlightList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const HighlightRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid #ececec;
  border-radius: 8px;
  background: #fff;
`;

const ColorDot = styled.span`
  width: 18px;
  height: 18px;
  border-radius: 4px;
  background: ${({ $color }) => $color};
  border: 1px solid #ddd;
  flex-shrink: 0;
`;

const HighlightInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const HighlightTitle = styled(Typography)`
  && {
    font-size: 14px;
    font-weight: 600;
    color: #333;
  }
`;

const HighlightMeta = styled(Typography)`
  && {
    font-size: 13px;
    color: #777;
  }
`;

const HighlightWarning = styled(Typography)`
  && {
    font-size: 12px;
    color: #b26a00;
    margin-top: 4px;
  }
`;

const HighlightActions = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const EmptyHighlights = styled(Typography)`
  && {
    color: #888;
    font-size: 14px;
    padding: 8px 0 12px;
  }
`;

const IDENTITY_KEYS = ["callNumber", "ra"];
const SETTINGS_COLUMN_KEYS = IDENTITY_KEYS.concat(
  ABSENCE_BOARD_SETTINGS_KEYS,
  GRADES_BOARD_COLUMN_KEY
);

const columnStateFromConfig = config => {
  const state = {};
  SETTINGS_COLUMN_KEYS.forEach(key => {
    state[key] = isBoardColumnVisible(config, key);
  });
  return state;
};

const buildColumnsPayload = columnState => {
  const columns = { name: { visible: true } };
  SETTINGS_COLUMN_KEYS.forEach(key => {
    columns[key] = { visible: columnState[key] };
  });
  return columns;
};

const APPLY_KIND_VISIBILITY = {
  callNumber: "callNumber",
  ra: "ra",
  name: null,
  absencesDiscipline: "absencesDiscipline",
  absencesTotal: "absencesTotal",
  absencesPercentDiscipline: "absencesPercentDiscipline",
  absencesPercentTotal: "absencesPercentTotal",
  gradeAny: GRADES_BOARD_COLUMN_KEY,
  gradeAverage: GRADES_BOARD_COLUMN_KEY
};

const kindLabel = (t, kind, examName) => {
  if (kind === "gradeAny" && examName) {
    return t("settings.generalBoard.highlights.kinds.gradeAnyNamed", {
      name: examName
    });
  }
  return t(`settings.generalBoard.highlights.kinds.${kind}`);
};

const describeApplyTo = (t, applyTo, examName) => {
  if (!applyTo || applyTo.mode !== "columns") {
    return t("settings.generalBoard.highlights.entireRow");
  }
  const columns = applyTo.columns || [];
  if (!columns.length) {
    return t("settings.generalBoard.highlights.selectedColumns");
  }
  return columns.map(kind => kindLabel(t, kind, examName)).join(", ");
};

const getHiddenApplyKinds = (applyTo, columnState) => {
  if (!applyTo || applyTo.mode !== "columns") return [];
  return (applyTo.columns || []).filter(kind => {
    const visibilityKey = APPLY_KIND_VISIBILITY[kind];
    if (!visibilityKey) return false;
    return !columnState[visibilityKey];
  });
};

const GeneralBoardSettings = () => {
  const { t } = useTranslation();
  const alert = useAlert();
  const [loading, setLoading] = useState(true);
  const [savingColumns, setSavingColumns] = useState(false);
  const [savingHighlights, setSavingHighlights] = useState(false);
  const [config, setConfig] = useState(DEFAULT_GENERAL_BOARD_CONFIG);
  const [initialColumns, setInitialColumns] = useState(
    columnStateFromConfig(DEFAULT_GENERAL_BOARD_CONFIG)
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const nextConfig = await GeneralBoardService.getConfig();
        if (cancelled) return;
        setConfig(nextConfig);
        setInitialColumns(columnStateFromConfig(nextConfig));
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          alert.show(t("settings.generalBoard.loadError"), {
            title: t("common.actions.error")
          });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [alert, t]);

  const currentColumns = columnStateFromConfig(config);
  const highlights = useMemo(() => getBoardHighlights(config), [config]);
  const hasColumnChanges = Object.keys(currentColumns).some(
    key => currentColumns[key] !== initialColumns[key]
  );
  const busy = savingColumns || savingHighlights;

  const handleColumnChange = key => event => {
    setConfig(
      normalizeGeneralBoardConfig(config, {
        columns: { [key]: { visible: event.target.checked } }
      })
    );
  };

  const persistHighlights = async (nextHighlights, { closeDialog } = {}) => {
    setSavingHighlights(true);
    try {
      const saved = await GeneralBoardService.saveConfig({
        highlights: normalizeHighlights(nextHighlights)
      });
      setConfig(
        normalizeGeneralBoardConfig(saved, {
          columns: buildColumnsPayload(currentColumns)
        })
      );
      if (closeDialog) {
        setDialogOpen(false);
        setEditingRule(null);
      }
      alert.show(t("settings.generalBoard.highlights.saved"), {
        title: t("common.actions.success")
      });
      return true;
    } catch (e) {
      console.error(e);
      alert.show(t("settings.generalBoard.highlights.saveError"), {
        title: t("common.actions.error")
      });
      return false;
    } finally {
      setSavingHighlights(false);
    }
  };

  const handleMove = async (id, direction) => {
    const index = highlights.findIndex(item => item.id === id);
    if (index < 0) return;
    const target = index + direction;
    if (target < 0 || target >= highlights.length) return;
    const next = highlights.slice();
    const [item] = next.splice(index, 1);
    next.splice(target, 0, item);
    await persistHighlights(next);
  };

  const handleSaveRule = async rule => {
    const next = editingRule
      ? highlights.map(item =>
          item.id === editingRule.id ? { ...rule, id: item.id } : item
        )
      : highlights.concat(rule);
    await persistHighlights(next, { closeDialog: true });
  };

  const handleConfirmDelete = async () => {
    const next = highlights.filter(item => item.id !== deleteId);
    const ok = await persistHighlights(next);
    if (ok) setDeleteId(null);
  };

  const describeRule = rule => {
    const examName =
      rule.base.kind === "gradeByName" && rule.base.name
        ? rule.base.name
        : "";
    const baseLabel = kindLabel(t, rule.base.kind);
    const applyPart = describeApplyTo(t, rule.applyTo, examName);
    if (isDesistenteKind(rule.base.kind)) {
      return `${baseLabel} · ${applyPart}`;
    }
    const operatorLabel = t(
      `settings.generalBoard.highlights.operators.${rule.operator}`
    );
    const namePart = examName ? ` "${examName}"` : "";
    let valuePart = "";
    if (rule.operator !== "empty" && rule.operator !== "notEmpty") {
      valuePart =
        rule.operator === "between"
          ? ` ${rule.value} – ${rule.valueTo}`
          : ` ${rule.value}`;
    }
    return `${baseLabel}${namePart} · ${operatorLabel}${valuePart} · ${applyPart}`;
  };

  const describeHiddenTargets = rule => {
    const examName =
      rule.base.kind === "gradeByName" && rule.base.name
        ? rule.base.name
        : "";
    const hidden = getHiddenApplyKinds(rule.applyTo, currentColumns);
    if (!hidden.length) return null;
    return t("settings.generalBoard.highlights.hiddenTargetsWarning", {
      columns: hidden.map(kind => kindLabel(t, kind, examName)).join(", ")
    });
  };

  const handleSaveColumns = async () => {
    setSavingColumns(true);
    try {
      const saved = await GeneralBoardService.saveConfig({
        columns: buildColumnsPayload(currentColumns),
        highlights
      });
      setConfig(saved);
      setInitialColumns(columnStateFromConfig(saved));
      alert.show(t("settings.generalBoard.saved"), {
        title: t("common.actions.success")
      });
    } catch (e) {
      console.error(e);
      alert.show(t("settings.generalBoard.saveError"), {
        title: t("common.actions.error")
      });
    } finally {
      setSavingColumns(false);
    }
  };

  if (loading) {
    return (
      <LoadContainer>
        <CircularProgress />
      </LoadContainer>
    );
  }

  return (
    <Box>
      <PageHeader>
        <Heading level={5}>{t("settings.generalBoard.pageTitle")}</Heading>
        <PageSubtitle>{t("settings.generalBoard.subtitle")}</PageSubtitle>
      </PageHeader>

      <Group>
        <Section>
          <SectionHeader>
            <SectionTitle>
              {t("settings.generalBoard.columnsTitle")}
            </SectionTitle>
            <SectionHint>{t("settings.generalBoard.columnsHint")}</SectionHint>
          </SectionHeader>
          <SectionBody>
            <FormControlLabel
              control={(
                <Checkbox
                  color="primary"
                  checked={currentColumns.callNumber}
                  onChange={handleColumnChange("callNumber")}
                  disabled={busy}
                />
              )}
              label={t("common.labels.callNumber")}
            />
            <FormControlLabel
              control={(
                <Checkbox
                  color="primary"
                  checked={currentColumns.ra}
                  onChange={handleColumnChange("ra")}
                  disabled={busy}
                />
              )}
              label={t("common.labels.ra")}
            />
            <FormControlLabel
              control={<Checkbox color="primary" checked disabled />}
              label={t("common.labels.student")}
            />
            <FormControlLabel
              control={(
                <Checkbox
                  color="primary"
                  checked={currentColumns.grades}
                  onChange={handleColumnChange(GRADES_BOARD_COLUMN_KEY)}
                  disabled={busy}
                />
              )}
              label={t("settings.generalBoard.columns.grades")}
            />
            {ABSENCE_BOARD_SETTINGS_KEYS.map(key => (
              <FormControlLabel
                key={key}
                control={(
                  <Checkbox
                    color="primary"
                    checked={currentColumns[key]}
                    onChange={handleColumnChange(key)}
                    disabled={busy}
                  />
                )}
                label={t(`settings.generalBoard.columns.${key}`)}
              />
            ))}
          </SectionBody>
          <FooterActions>
            <Button
              color="primary"
              variant="contained"
              onClick={handleSaveColumns}
              disabled={busy || !hasColumnChanges}
            >
              {savingColumns
                ? t("common.actions.saving")
                : t("common.actions.save")}
            </Button>
          </FooterActions>
        </Section>

        <Section>
          <SectionHeader>
            <SectionTitle>
              {t("settings.generalBoard.highlights.title")}
            </SectionTitle>
            <SectionHint>{t("settings.generalBoard.highlights.hint")}</SectionHint>
          </SectionHeader>
          <SectionBody>
            {highlights.length === 0 ? (
              <EmptyHighlights>
                {t("settings.generalBoard.highlights.empty")}
              </EmptyHighlights>
            ) : (
              <HighlightList>
                {highlights.map((rule, index) => {
                  const hiddenWarning = describeHiddenTargets(rule);
                  return (
                    <HighlightRow key={rule.id}>
                      <ColorDot $color={rule.color} />
                      <HighlightInfo>
                        <HighlightTitle>
                          {t("settings.generalBoard.highlights.ruleLabel", {
                            index: index + 1
                          })}
                        </HighlightTitle>
                        <HighlightMeta>{describeRule(rule)}</HighlightMeta>
                        {hiddenWarning && (
                          <HighlightWarning>{hiddenWarning}</HighlightWarning>
                        )}
                      </HighlightInfo>
                      <HighlightActions>
                        <IconButton
                          size="small"
                          disabled={busy || index === 0}
                          onClick={() => handleMove(rule.id, -1)}
                          aria-label={t(
                            "settings.generalBoard.highlights.moveUp"
                          )}
                        >
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          disabled={busy || index === highlights.length - 1}
                          onClick={() => handleMove(rule.id, 1)}
                          aria-label={t(
                            "settings.generalBoard.highlights.moveDown"
                          )}
                        >
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          disabled={busy}
                          onClick={() => {
                            setEditingRule(rule);
                            setDialogOpen(true);
                          }}
                          aria-label={t("common.actions.edit")}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          disabled={busy}
                          onClick={() => setDeleteId(rule.id)}
                          aria-label={t("common.actions.delete")}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </HighlightActions>
                    </HighlightRow>
                  );
                })}
              </HighlightList>
            )}
          </SectionBody>
          <FooterActions>
            <Button
              variant="outlined"
              color="primary"
              disabled={busy}
              onClick={() => {
                setEditingRule(null);
                setDialogOpen(true);
              }}
            >
              {t("settings.generalBoard.highlights.add")}
            </Button>
          </FooterActions>
        </Section>
      </Group>

      <HighlightRuleDialog
        open={dialogOpen}
        rule={editingRule}
        saving={savingHighlights}
        columnVisibility={currentColumns}
        onClose={() => {
          if (savingHighlights) return;
          setDialogOpen(false);
          setEditingRule(null);
        }}
        onSave={handleSaveRule}
      />

      <ConfirmAlert
        open={Boolean(deleteId)}
        title={t("settings.generalBoard.highlights.deleteTitle")}
        text={t("settings.generalBoard.highlights.deleteConfirm")}
        onClose={() => {
          if (savingHighlights) return;
          setDeleteId(null);
        }}
        onConfirm={handleConfirmDelete}
        confirmButtonColor="primary"
      />
    </Box>
  );
};

export default GeneralBoardSettings;
